/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import IEnergyManagementProps from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import useMounted from '../../../shared/useMount/useMount';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import CarbonScopeTwoDetailsComponent from '../../CarbonSiteViewDetailsComponent/CarbonScopeTwoDetailWidget/CarbonScopeTwoDetailWidget';
import { UtilityType } from '../../../shared/Utils/Constants';
import {
  formattedDate,
  returnFormattedDate
} from '../../../shared/Utils/Utils';
import PortfolioCarbonScopeTwoSavingsByMeterComponent from '../PortfolioCarbonScopeTwoSavingsBySiteWidget/PortfolioCarbonScopeTwoSavingsBySiteComponent';
import { getCarbonVDEndDateBasedOnSub } from '../../../shared/Utils/CarbonViewDetailsUtils';
import { EmaasSubscriptionType } from '../../../shared/Enums';
import { ApiContext } from '../../../../context/ApiContext';
import moment from 'moment';
import PortfolioCarbonScopeTwoSavingsBySiteComponentSeparate from '../PortfolioCarbonScopeTwoSavingsBySiteWidget/PortfolioCarbonScopeTwoSavingsBySiteComponentSeparate';
import CarbonScopeTwoDetailWidgetSeparatePortfolio from '../../CarbonSiteViewDetailsComponent/CarbonScopeTwoDetailWidget/CarbonScopeTwoDetailWidgetSeparatePortfolio';

const PortfolioCarbonScopeTwoDetailsComponent = (
  props: IEnergyManagementProps
) => {
  const { bearerToken, requestContext } = props;

  const apiContext = useContext(ApiContext);
  const dataSource = requestContext.context.dataSource || '';
  const isSitePremium = apiContext.toggleSeparateApi;

  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);
  const getAdjustedEndDate = (userEndDate: string | Date) => {
    // Parse the user's end date
    const userDate = new Date(userEndDate);
    const currentDate = new Date();

    // Check if the user's end date month and year are the same as the current month and year
    if (
      userDate.getMonth() === currentDate.getMonth() &&
      userDate.getFullYear() === currentDate.getFullYear()
    ) {
      // Subtract one month from the user's end date
      userDate.setMonth(userDate.getMonth() - 1);
    }

    return userDate.toISOString();
  };

  const isDateRangeValid = () => {
    const stDate = requestContext.context.startTime
      ? formattedDate(requestContext.context.startTime)
      : '';
    const eDate = moment(
      getCarbonVDEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!
      ),
      'YYYY-MM-DD'
    );
    return eDate.diff(stDate, 'days') <= 365;
  };

  const connectorConfig = {
    name: isSitePremium
      ? 'portfolio-utility-electricity-summary'
      : 'portfolio-utility-electricity',
    options: {
      startDate: requestContext.context.startTime
        ? formattedDate(requestContext.context.startTime)
        : '',
      endDate: getCarbonVDEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!,
        dataSource
      ),
      allDetails: 'true',
      scopeType: 'scope2',
      dataSource: isSitePremium ? dataSource : ''
    }
  };
  const connectorConfigForGraph = {
    name: 'portfolio-utility-electricity-graph',
    options: {
      startDate: requestContext.context.startTime
        ? formattedDate(requestContext.context.startTime)
        : '',
      endDate: getCarbonVDEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!,
        dataSource
      ),
      allDetails: 'true',
      scopeType: 'scope2',
      dataSource
    }
  };

  const connectorConfigForTable = {
    name: 'portfolio-utility-electricity-table',
    options: {
      startDate: requestContext.context.startTime
        ? formattedDate(requestContext.context.startTime)
        : '',
      endDate: getCarbonVDEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!,
        dataSource
      ),
      allDetails: 'true',
      groupType: 'scopetype',
      scopeType: 'scope2',
      dataSource
    }
  };

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  let dataConnectorForGraph: any;
  dataConnectorForGraph = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigForGraph,
      requestContext,
      bearerToken
    },
    isSitePremium
  )[0];

  let dataConnectorForTable: any;
  dataConnectorForTable = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigForTable,
      requestContext,
      bearerToken
    },
    isSitePremium
  )[0];

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  useEffect(() => {
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        dataConnector &&
          dataConnector.actions &&
          dataConnector.actions.getAll();

        if (isSitePremium) {
          dataConnectorForGraph &&
            dataConnectorForGraph.actions &&
            dataConnectorForGraph.actions.getAll();
          dataConnectorForTable &&
            dataConnectorForTable.actions &&
            dataConnectorForTable.actions.getAll();
        }
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext, dataSource]);
  const UtilityBillType = UtilityType.Electricity;

  return (
    <div className="total-emissions-scope-two-details">
      {isSitePremium ? (
        <CarbonScopeTwoDetailWidgetSeparatePortfolio
          dataConnector={dataConnector}
          dataConnectorForGraph={dataConnectorForGraph}
          dataConnectorForTable={dataConnectorForTable}
          dataSourceType={props.dataSourceType ? props.dataSourceType : ''}
        />
      ) : (
        <CarbonScopeTwoDetailsComponent
          dataConnector={dataConnector}
          dataSourceType={props.dataSourceType ? props.dataSourceType : ''}
        />
      )}
      {isSitePremium ? (
        <PortfolioCarbonScopeTwoSavingsBySiteComponentSeparate
          dataConnector={dataConnector}
          dataConnectorForTable={dataConnectorForTable}
          UtilityBillType={UtilityBillType}
        />
      ) : (
        <PortfolioCarbonScopeTwoSavingsByMeterComponent
          dataConnector={dataConnector}
          UtilityBillType={UtilityBillType}
        />
      )}
    </div>
  );
};
export default PortfolioCarbonScopeTwoDetailsComponent;
